/**
 * Sessions resource - /api/sessions endpoints
 */
import { getLogger } from '@qwickapps/logging';
const log = getLogger('QwickForge.MiddlewareClient.SessionsResource');
export class SessionsResource {
    http;
    constructor(http) {
        this.http = http;
    }
    /**
     * Create a new session
     */
    async create(title, metadata, projectId, deviceId) {
        log.debug('Creating session', { title, projectId, deviceId });
        const response = await this.http.post('/api/sessions', {
            title,
            projectId,
            deviceId,
            metadata,
        });
        log.info('Session created', { sessionId: response.session.id, title });
        return response.session;
    }
    /**
     * Get session by ID
     */
    async get(sessionId) {
        try {
            log.debug('Getting session', { sessionId });
            const session = await this.http.get(`/api/sessions/${sessionId}`);
            return session;
        }
        catch (error) {
            if (error.statusCode === 404) {
                log.debug('Session not found', { sessionId });
                return undefined;
            }
            throw error;
        }
    }
    /**
     * List sessions with pagination
     */
    async list(userId, limit = 50, offset = 0) {
        const params = new URLSearchParams();
        params.set('limit', limit.toString());
        params.set('offset', offset.toString());
        if (userId)
            params.set('userId', userId);
        log.debug('Listing sessions', { userId, limit, offset });
        const response = await this.http.get(`/api/sessions?${params.toString()}`);
        // Transform ListSessionsResponse to PaginatedResponse format
        const paginatedResponse = {
            data: response.sessions || [],
            total: response.pagination?.total || 0,
            limit: response.pagination?.limit || limit,
            offset: response.pagination?.offset || offset,
        };
        log.debug('Sessions listed', { count: paginatedResponse.data.length, total: paginatedResponse.total });
        return paginatedResponse;
    }
    /**
     * Update session metadata
     */
    async updateMetadata(sessionId, metadata) {
        log.debug('Updating session metadata', { sessionId });
        // Note: sessionId is included in body due to schema requirement, even though it's also in URL
        await this.http.patch(`/api/sessions/${sessionId}`, { sessionId, metadata });
        log.debug('Session metadata updated', { sessionId });
    }
    /**
     * Update session
     */
    async update(sessionId, updates) {
        log.debug('Updating session', { sessionId, updates });
        // Note: sessionId is included in body due to schema requirement, even though it's also in URL
        await this.http.patch(`/api/sessions/${sessionId}`, { ...updates, sessionId });
        log.debug('Session updated', { sessionId });
    }
    /**
     * Close a session
     */
    async close(sessionId, endedAt) {
        log.debug('Closing session', { sessionId });
        // Note: sessionId is included in body due to schema requirement, even though it's also in URL
        await this.http.patch(`/api/sessions/${sessionId}`, {
            sessionId,
            endedAt: endedAt.toISOString(),
        });
        log.info('Session closed', { sessionId });
    }
    /**
     * Get session timeline (prompts and executions)
     */
    async getTimeline(sessionId, options) {
        const params = new URLSearchParams();
        if (options?.limit)
            params.set('limit', options.limit.toString());
        if (options?.offset)
            params.set('offset', options.offset.toString());
        if (options?.types)
            params.set('types', options.types.join(','));
        log.debug('Getting session timeline', { sessionId, options });
        const response = await this.http.get(`/api/sessions/${sessionId}/timeline?${params.toString()}`);
        log.debug('Session timeline retrieved', {
            sessionId,
            itemCount: response.timeline?.length || 0
        });
        return response;
    }
    /**
     * Get session statistics (batch)
     */
    async getStats(sessionIds) {
        log.debug('Getting session stats', { sessionCount: sessionIds.length });
        const response = await this.http.post('/api/sessions/stats/batch', { sessionIds });
        const statsMap = new Map();
        response.stats.forEach(stat => {
            statsMap.set(stat.sessionId, {
                promptCount: stat.prompt_count,
                executionCount: stat.execution_count,
                totalInputTokens: stat.total_input_tokens || 0,
                totalOutputTokens: stat.total_output_tokens || 0,
                totalCostUsd: stat.total_cost_usd || 0,
            });
        });
        log.debug('Session stats retrieved', { sessionCount: statsMap.size });
        return statsMap;
    }
}
//# sourceMappingURL=SessionsResource.js.map